import { ChangeDetectionStrategy, Component, computed, effect, inject, input, LOCALE_ID, output, signal } from '@angular/core';
import { CommonModule, formatDate } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { ExcelModule, GridDataResult, GridModule, PageChangeEvent } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';

export interface IdentityEntityColumn {
  field: string;
  title: string;
  width?: number;
  /**
   * When 'date', the cell is piped through Angular's DatePipe.
   *
   * 'boolean' renders a tick or a cross instead of the raw true/false. Deliberately
   * not green-for-true: in the privileged-accounts table a true means "this person
   * CAN reset passwords", which is capability and exposure, not something passing.
   * A green tick would read as "all good" on exactly the rows that deserve the most
   * attention. Both marks stay neutral and carry a text label for screen readers;
   * the Excel export still receives the raw value.
   *
   * 'id' is for long identifiers with no spaces, such as a 64-character backfilled
   * alert id: monospace and free to wrap at any character, because the grid would
   * otherwise clip everything past the column width.
   */
  type?: 'text' | 'number' | 'date' | 'badge' | 'boolean' | 'id';
  /**
   * Text for a 'date' cell when the field is empty. Opt-in per column: without it the
   * DatePipe renders nothing, which is what every date column does today and what most
   * of them should keep doing.
   *
   * It exists for Last Password Change on the leaked-credentials table, where a blank
   * cell would read as a fact about the identity ("never changed it") when the truth is
   * that the directory never reported one. The Excel export is unaffected — cell
   * templates do not reach it — so the exported cell stays empty.
   */
  emptyText?: string;
}

export interface IdentityEntityDialogConfig {
  title: string;
  /**
   * Let the server do the paging, through the shared `app-datatable` grid.
   *
   * Off by default: most drilldowns are a handful of rows and slicing them in the
   * browser is simpler and instant. Turn it on where the list is unbounded — the
   * monitored-identities table is every account in the directory, and the largest
   * production tenant has 12,905, around 1.8 MB in one response.
   */
  serverSide?: boolean;
  /** Optional one-line context shown in the grid toolbar. */
  subtitle?: string;
  apiUrl: string;
  columns: IdentityEntityColumn[];
  /** Optional query params appended to the request (e.g. { date: '2026-06-08' }). */
  queryParams?: Record<string, string | number>;
  /** Filename for the Excel export. Defaults to title-based slug. */
  exportFileName?: string;
}

@Component({
  selector: 'app-identity-entity-dialog',
  standalone: true,
  imports: [CommonModule, DialogsModule, GridModule, ExcelModule, ButtonsModule, DatatableComponent],
  templateUrl: './identity-entity-dialog.component.html',
  styleUrl: './identity-entity-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IdentityEntityDialogComponent {
  private http = inject(HttpClient);
  // `formatDate` rather than the DatePipe: the pipe would have to be listed in this
  // component's `providers` to be injectable, and the specs override the component
  // metadata wholesale to blank the template, which would take those providers with
  // it. LOCALE_ID has a platform default, so nothing extra is needed anywhere.
  private locale = inject(LOCALE_ID);

  /**
   * One date format for both rendering paths.
   *
   * The Kendo cell template and the shared datatable's `render` callback share no
   * template, and they had drifted: the grid showed "Aug 10, 2026, 5:50 AM" while the
   * server-side list showed "8/10/2026, 5:50:10 AM" - locale-dependent and with
   * seconds. Same column, two looks, decided by whether the list happens to page on
   * the server.
   */
  public readonly dateFormat = 'MMM d, y, h:mm a';

  public config = input<IdentityEntityDialogConfig | null>(null);
  public visible = input<boolean>(false);
  public close = output<void>();

  public rows = signal<Record<string, unknown>[]>([]);
  public isLoading = signal<boolean>(false);
  public pageSize = signal<number>(25);
  public skip = signal<number>(0);

  /** Slice of rows visible at current pagination state — feeds kendo-grid. */
  public gridData = computed<GridDataResult>(() => ({
    data: this.rows().slice(this.skip(), this.skip() + this.pageSize()),
    total: this.rows().length,
  }));

  /**
   * Every row, for the Excel export — same `fetchData` hook AlertDataDialog uses.
   *
   * Without it the export takes whatever is bound to the grid, and that is
   * `gridData()`, a single page. The customer would get 25 rows in a file that
   * looks complete, with nothing on screen saying otherwise. Pagination here is
   * client-side and the rows are already in memory, so there is nothing to fetch.
   */
  public allData = (): GridDataResult => ({
    data: this.rows(),
    total: this.rows().length,
  });

  /**
   * The same columns, in the shape the shared grid wants.
   *
   * `name` is what it sends back as the `ordering` parameter, so it has to match the
   * field the server sorts on. Dates are formatted here because that grid renders raw
   * values, unlike the Kendo one which pipes them.
   */
  public datatableColumns = computed(() =>
    (this.config()?.columns ?? []).map((col) => ({
      data: col.field,
      name: col.field,
      title: col.title,
      render:
        col.type === 'date'
          ? (value: string) =>
              value ? formatDate(value, this.dateFormat, this.locale) : (col.emptyText ?? '')
          : undefined,
    }))
  );

  public exportFileName = computed(() => {
    const cfg = this.config();
    if (cfg?.exportFileName) return cfg.exportFileName;
    const slug = (cfg?.title ?? 'identity-detail')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');
    return `${slug}.xlsx`;
  });

  constructor() {
    effect(
      () => {
        const cfg = this.config();
        if (this.visible() && cfg) {
          this.fetch(cfg);
        }
      },
      { allowSignalWrites: true }
    );
  }

  public onPageChange(event: PageChangeEvent): void {
    this.skip.set(event.skip);
    this.pageSize.set(event.take);
  }

  private fetch(cfg: IdentityEntityDialogConfig): void {
    // The server-side grid issues its own paged requests; fetching everything here
    // first would defeat the point of paging it.
    if (cfg.serverSide) return;

    this.isLoading.set(true);
    this.rows.set([]);
    this.skip.set(0);
    let params = new HttpParams();
    if (cfg.queryParams) {
      for (const [k, v] of Object.entries(cfg.queryParams)) {
        params = params.set(k, String(v));
      }
    }
    this.http.get<Record<string, unknown>[]>(cfg.apiUrl, { params }).subscribe({
      next: (res) => {
        this.rows.set(res ?? []);
        this.isLoading.set(false);
      },
      error: (err) => {
        console.error('IdentityEntityDialog fetch failed:', err);
        this.isLoading.set(false);
      },
    });
  }

  public onClose(): void {
    this.close.emit();
  }
}
